import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { portalApi } from "@/lib/portalApi";
import { toast } from "sonner";
import { Send, AlertCircle } from "lucide-react";

const CATEGORIES = [
  { value: "food", label: "Food & nutrition" },
  { value: "housing", label: "Housing" },
  { value: "healthcare", label: "Healthcare" },
  { value: "mental_health", label: "Mental health" },
  { value: "maternal", label: "Maternal health" },
  { value: "childcare", label: "Childcare" },
  { value: "employment", label: "Employment" },
  { value: "transportation", label: "Transportation" },
  { value: "education", label: "Education / training" },
  { value: "legal", label: "Legal help" },
  { value: "faith", label: "Faith community" },
  { value: "other", label: "Something else" },
];

const URGENCIES = [
  { value: "now",        label: "Right now — today" },
  { value: "this_week",  label: "This week" },
  { value: "this_month", label: "This month" },
];

export default function PortalRequestHelp() {
  const navigate = useNavigate();
  const [category, setCategory] = useState("");
  const [urgency, setUrgency] = useState("this_week");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please share a few words so we know how to help.");
      return;
    }
    setSubmitting(true);
    try {
      await portalApi.post("/portal/request-help", {
        category: category || null,
        urgency,
        message: message.trim(),
      });
      toast.success("Thanks — your Care Navigator will follow up soon.");
      navigate("/portal", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="portal-request-page">
      <div>
        <p className="text-xs uppercase tracking-wider text-[#7E937D] font-medium">Ask for help</p>
        <h1 className="font-display text-3xl font-semibold text-[#0F172A] mt-1">What's going on?</h1>
        <p className="text-sm text-[#334155] mt-2">
          Tell us what kind of support would help most and we'll follow up.
          For an immediate emergency, please call 911 or 988.
        </p>
      </div>

      <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-[#92400E] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[#0F172A]">
          This form isn't for emergencies. If you or someone else is in danger,
          please call <a href="tel:911" className="underline font-medium">911</a>.
          For a mental-health crisis, call or text <a href="tel:988" className="underline font-medium">988</a>.
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E5E5E0] rounded-2xl p-5 space-y-5">
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">What kind of help do you need?</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm"
            data-testid="portal-request-category"
          >
            <option value="">Choose one…</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">How soon do you need help?</label>
          <div className="grid sm:grid-cols-3 gap-2">
            {URGENCIES.map((u) => (
              <button
                key={u.value}
                type="button"
                onClick={() => setUrgency(u.value)}
                className={`px-4 py-3 rounded-xl border text-sm font-medium text-left transition-colors ${
                  urgency === u.value
                    ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                    : "bg-white text-[#0F172A] border-[#E5E5E0] hover:border-[#1E3A8A]"
                }`}
                data-testid={`portal-request-urgency-${u.value}`}
              >
                {u.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">Tell us a little more</label>
          <textarea
            rows={6}
            maxLength={5000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Share what's going on. Your Care Navigator will see this."
            className="w-full px-3 py-3 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm"
            data-testid="portal-request-message"
            required
          />
          <div className="text-right text-xs text-[#7E937D] mt-1">{message.length} / 5000</div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] disabled:opacity-50"
          data-testid="portal-request-submit"
        >
          <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send to my Care Navigator"}
        </button>
      </form>
    </div>
  );
}
